import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Add, Edit, Delete, Article, Category } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { MediBlogArticle, MediBlogCategory } from "../../../../services/mediBlog";

interface MediBlogTabProps {
  articles: MediBlogArticle[];
  categories: MediBlogCategory[];
  onEditArticle: (article?: MediBlogArticle) => void;
  onDeleteArticle: (id: string) => void;
  onEditCategory: (category?: MediBlogCategory) => void;
  onDeleteCategory: (id: string) => void;
}

const statusColor: Record<string, "default" | "success" | "warning"> = {
  draft: "warning",
  published: "success",
  archived: "default",
};

const MediBlogTab: React.FC<MediBlogTabProps> = ({
  articles,
  categories,
  onEditArticle,
  onDeleteArticle,
  onEditCategory,
  onDeleteCategory,
}) => {
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState(0);

  const getCategoryName = (categoryId?: string | null) => {
    if (!categoryId) return "—";
    const cat = categories.find((c) => c.id === categoryId);
    return cat?.name_en ?? "—";
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          {t("admin.contentManagement.mediblog.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => (subTab === 0 ? onEditArticle() : onEditCategory())}
          sx={{
            background: "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {subTab === 0
            ? t("admin.contentManagement.mediblog.addArticle")
            : t("admin.contentManagement.mediblog.addCategory")}
        </Button>
      </Box>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab icon={<Article />} iconPosition="start" label={t("admin.contentManagement.mediblog.articles")} />
        <Tab icon={<Category />} iconPosition="start" label={t("admin.contentManagement.mediblog.categories")} />
      </Tabs>

      {subTab === 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              xl: "repeat(3, 1fr)",
            },
            gap: 2,
          }}
        >
          {articles.length === 0 && (
            <Typography color="text.secondary" sx={{ gridColumn: "1 / -1", py: 4, textAlign: "center" }}>
              {t("admin.contentManagement.mediblog.noArticles")}
            </Typography>
          )}
          {articles.map((article) => (
            <Card key={article.id} sx={{ display: "flex", flexDirection: "column" }}>
              {article.featured_image && (
                <Box
                  sx={{
                    height: 140,
                    backgroundImage: `url(${article.featured_image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              )}
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, gap: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: "1rem", flex: 1 }}>
                    {article.title_en}
                  </Typography>
                  <Chip
                    label={t(`admin.contentManagement.mediblog.status.${article.status ?? "draft"}`)}
                    color={statusColor[article.status ?? "draft"]}
                    size="small"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  /mediblog/{article.slug}
                </Typography>
                <Chip
                  label={getCategoryName(article.category_id)}
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start", mb: 2 }}
                />
                {article.excerpt_en && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                    {article.excerpt_en.slice(0, 120)}
                    {article.excerpt_en.length > 120 ? "…" : ""}
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                  <Tooltip title={t("admin.contentManagement.edit")}>
                    <IconButton size="small" onClick={() => onEditArticle(article)} sx={{ color: "#00d4ff" }}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("admin.contentManagement.delete")}>
                    <IconButton
                      size="small"
                      onClick={() => article.id && onDeleteArticle(article.id)}
                      sx={{ color: "#ff3366" }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {subTab === 1 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.contentManagement.fields.title")}</TableCell>
                <TableCell>{t("admin.contentManagement.mediblog.slug")}</TableCell>
                <TableCell>{t("admin.contentManagement.fields.displayOrder")}</TableCell>
                <TableCell>{t("admin.contentManagement.active")}</TableCell>
                <TableCell align="right">{t("admin.contentManagement.edit")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    {t("admin.contentManagement.mediblog.noCategories")}
                  </TableCell>
                </TableRow>
              )}
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name_en}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {category.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>{category.display_order ?? 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        category.is_active
                          ? t("admin.contentManagement.active")
                          : t("admin.contentManagement.inactive")
                      }
                      color={category.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => onEditCategory(category)} sx={{ color: "#00d4ff" }}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => category.id && onDeleteCategory(category.id)}
                      sx={{ color: "#ff3366" }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MediBlogTab;
